import React from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  Position,
} from '@xyflow/react';
import { ConfigureIcon, NotPossibleIcon } from 'components/routes/flowIcons';
import { FLOW_COLORS, FLOW_HOVER_COLORS } from './flowMapTypes';
import { useTranslation } from 'react-i18next';

const stateWord: Record<string, string> = {
  enabled: 'Enabled',
  disabled: 'Disabled',
  'not-enableable': 'Not available',
};

const DirectionEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}: any) => {
  const { t } = useTranslation();
  // Hovering either the line or its button lights both, so the whole arrow reads as
  // one clickable thing.
  const [hovered, setHovered] = React.useState(false);
  const state = data?.state || 'disabled';
  // Phone layout: right-angle runs down the rail between the stacked cards.
  const pathFor = data?.vertical ? getSmoothStepPath : getBezierPath;
  const [path, labelX, labelY] = pathFor({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition: sourcePosition || Position.Right,
    targetPosition: targetPosition || Position.Left,
  });
  const baseStroke =
    FLOW_COLORS[state as keyof typeof FLOW_COLORS] || FLOW_COLORS.disabled;
  // A slightly different shade on hover is the only cue: size and weight stay put.
  const stroke = hovered
    ? FLOW_HOVER_COLORS[state as keyof typeof FLOW_HOVER_COLORS] || baseStroke
    : baseStroke;
  const strokeWidth = state === 'enabled' ? 2.5 : 2;
  const strokeDasharray = state === 'enabled' ? undefined : '5 4';
  const onClick = () => data?.onSelect?.(data.destinationId, data.direction);
  // The arrow's colour already says what state the direction is in, so the only
  // thing riding on the line is the button that opens its configuration — crossed
  // out when the direction can never be enabled.
  const impossible = state === 'not-enableable';
  const flowLabel =
    data?.direction === 'out' ? t('Sends data') : t('Receives data');
  const title = `${flowLabel} · ${t(stateWord[state] as any)} — ${impossible ? t('why not') : t('configure')}`;

  return (
    <>
      {/* fat transparent hit area so the line itself is clickable */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={18}
        style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      />
      <BaseEdge
        id={id}
        path={path}
        style={{ stroke, strokeWidth, strokeDasharray }}
        markerEnd={`url(#flow-arrow-${state}${hovered ? '-hover' : ''})`}
      />
      <EdgeLabelRenderer>
        {/* The transform lives on a wrapper so the label and its badge move together. */}
        <div
          className="pointer-events-auto absolute"
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
          }}
        >
          {/* Arrows never carry a "needs setup" badge — every action is
                        configured on its own node, so the gap badges that node. */}
          <button
            type="button"
            onClick={onClick}
            aria-label={title}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className={`relative flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border bg-white transition-colors duration-150 ${
              // Focus ring matches the direction's state — a green
              // ring on a disabled flow reads as "this is on".
              data?.isSelected
                ? state === 'enabled'
                  ? hovered
                    ? 'border-green-12 bg-teal-t10 text-green-12 ring-2 ring-green-12'
                    : 'border-green-1 text-green-1 ring-2 ring-green-1'
                  : hovered
                    ? 'border-unlisted-medium-gray bg-teal-t10 text-ui-text-primary-text ring-2 ring-unlisted-medium-gray'
                    : 'border-unlisted-medium-gray text-ui-text-primary-text ring-2 ring-unlisted-medium-gray'
                : // Driven by the shared hover state, not CSS :hover, so the
                  // button lights up when the line is hovered too.
                  state === 'enabled'
                  ? hovered
                    ? 'border-green-12 bg-teal-t10 text-green-12'
                    : 'border-green-1 text-green-1'
                  : hovered
                    ? 'border-unlisted-medium-gray bg-teal-t10 text-ui-text-primary-text'
                    : 'border-unlisted-gray-lines text-ui-text-secondary-text'
            }`}
          >
            {impossible ? <NotPossibleIcon /> : <ConfigureIcon />}
          </button>
          {/* Rules are per flow, so their badge rides on the arrow — above
                        it, away from the second arrow running underneath. One rule is
                        named; several collapse to a count, so the pill stays short
                        enough to fit between the cards on narrow screens. */}
          {(data?.hasRules || data?.hasDeviceFilter) && (
            <span className="absolute bottom-full left-1/2 mb-1 -translate-x-1/2 whitespace-nowrap rounded-full bg-teal-t10 px-2 py-0.5 text-[11px] font-semibold text-green-1">
              {data?.hasRules && data?.hasDeviceFilter
                ? t('{{count}} rules applied', { count: 2 })
                : data?.hasRules
                  ? t('Field mapping')
                  : t('Source filter')}
            </span>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

export default DirectionEdge;
